import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import { TenantSelector } from './TenantSelector';
import { Breadcrumbs, BreadcrumbItem } from './Breadcrumbs';

interface HeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  actions?: React.ReactNode;
  /** Se true, esconde TenantSelector e o slot de ações; mostra só menu, quota e usuário */
  compactTopbar?: boolean;
}

interface TenantQuota {
  maxMessagesPerMonth: number;
  messagesSentThisMonth: number;
  maxContacts: number;
  maxCampaigns: number;
  billingCycleDay: number;
}

export function Header({ title, subtitle, breadcrumbs, actions, compactTopbar }: HeaderProps) {
  const { settings, loading } = useSettings();
  const { user, logout } = useAuth();
  const { isOpen, toggle, isMobile } = useSidebar();
  const [quota, setQuota] = useState<TenantQuota | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // Botão de menu sempre visível na topbar: mobile = abrir/fechar overlay; desktop = recolher/expandir sidebar
  const showMenuButton = true;

  // Carregar quota do tenant
  const loadQuota = async () => {
    if (!user?.tenantId && user?.role !== 'SUPERADMIN') return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const tenantId = user?.role === 'SUPERADMIN' 
        ? localStorage.getItem('superadmin_selected_tenant') 
        : user?.tenantId;
      
      if (!tenantId) return;
      
      const response = await fetch(`/api/tenants/${tenantId}/quota`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setQuota(data.quota);
      }
    } catch (error) {
      console.error('Erro ao carregar quota:', error);
    }
  };

  useEffect(() => {
    loadQuota();
    
    // Listener para mudança de tenant (superadmin)
    const handleTenantChange = () => loadQuota();
    window.addEventListener('superadmin-tenant-changed', handleTenantChange);
    
    // Listener para atualização de quota (quando campanha é criada/iniciada)
    const handleQuotaUpdate = () => loadQuota();
    window.addEventListener('quota-updated', handleQuotaUpdate);
    
    // Listener para quando página ganha foco (usuário volta para a aba)
    const handleFocus = () => loadQuota();
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('superadmin-tenant-changed', handleTenantChange);
      window.removeEventListener('quota-updated', handleQuotaUpdate);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user]);

  const getUserRoleText = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'Administrador';
      case 'USER':
        return 'Usuário';
      default:
        return 'Usuário';
    }
  };

  // Calcular porcentagem de uso de mensagens
  const getMessageUsagePercent = () => {
    if (!quota) return 0;
    const max = quota.maxMessagesPerMonth || 0;
    const sent = quota.messagesSentThisMonth || 0;
    if (max === 0) return 0;
    return Math.min(100, Math.round((sent / max) * 100));
  };

  const getUsageColor = () => {
    const percent = getMessageUsagePercent();
    if (percent >= 90) return 'from-red-500 to-red-600';
    if (percent >= 70) return 'from-yellow-400 to-orange-500';
    return 'from-emerald-400 to-teal-500';
  };

  const getUsageBgColor = () => {
    const percent = getMessageUsagePercent();
    if (percent >= 90) return 'bg-red-50 border-red-200';
    if (percent >= 70) return 'bg-orange-50 border-orange-200';
    return 'bg-emerald-50 border-emerald-200';
  };

  const getUsageTextColor = () => {
    const percent = getMessageUsagePercent();
    if (percent >= 90) return 'text-red-700';
    if (percent >= 70) return 'text-orange-700';
    return 'text-emerald-700';
  };

  const getUsageIcon = () => {
    const percent = getMessageUsagePercent();
    if (percent >= 90) return '🔴';
    if (percent >= 70) return '🟡';
    return '🟢';
  };

  const formatQuota = () => {
    if (!quota) return '';
    const sent = quota.messagesSentThisMonth || 0;
    const max = quota.maxMessagesPerMonth || 0;
    if (max === 0) return `${sent.toLocaleString()} / ∞`;
    return `${sent.toLocaleString()} / ${max.toLocaleString()}`;
  };

  return (
    <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
      <div className="flex items-center justify-between gap-2">
        {/* Botão menu na topbar: mobile = hamburger (abrir/fechar); desktop = setas (recolher/expandir sidebar) */}
        {showMenuButton && (
          <button
            type="button"
            onClick={toggle}
            className="flex items-center justify-center w-10 h-10 min-w-[2.5rem] min-h-[2.5rem] rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200 transition-colors shrink-0 order-first"
            aria-label={isOpen ? (isMobile ? 'Fechar menu' : 'Recolher menu') : 'Abrir menu'}
            title={isOpen ? (isMobile ? 'Fechar menu' : 'Recolher menu') : 'Expandir menu'}
          >
            {isMobile ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                )}
              </svg>
            )}
          </button>
        )}

        {/* Ações da direita: ações da página; depois divisor + quota + usuário no canto */}
        <div className="flex items-center gap-4 flex-1 min-w-0 justify-end">
          {/* Tenant Selector (oculto em topbar compacta, ex.: página Conectar WhatsApp) */}
          {!compactTopbar && <TenantSelector />}

          {/* Ações customizadas (Nova Sessão, Atualizar, etc.) */}
          {!compactTopbar && actions && (
            <div className="flex items-center gap-2 shrink-0">
              {actions}
            </div>
          )}

          {/* Contador de mensagens + usuário (sempre juntos no canto direito) */}
          <div className="flex items-center gap-4 shrink-0">
            {/* Indicador de Quota de Mensagens */}
            {quota && (
              <button 
                onClick={loadQuota}
                className={`hidden md:flex items-center gap-3 px-4 py-2 rounded-xl border shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer ${getUsageBgColor()}`}
                title={`Mensagens enviadas este mês: ${formatQuota()} - Clique para atualizar`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">{getUsageIcon()}</span>
                  <div className="flex flex-col items-start">
                    <span className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Mensagens</span>
                    <span className={`text-sm font-bold ${getUsageTextColor()}`}>{formatQuota()}</span>
                  </div>
                </div>
                {quota.maxMessagesPerMonth > 0 && (
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-xs font-bold ${getUsageTextColor()}`}>{getMessageUsagePercent()}%</span>
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                      <div 
                        className={`h-full rounded-full bg-gradient-to-r ${getUsageColor()} transition-all duration-500 ease-out`}
                        style={{ width: `${getMessageUsagePercent()}%` }}
                      />
                    </div>
                  </div>
                )}
              </button>
            )}

            {/* Usuário com dropdown (Sair no submenu) */}
            <div className="border-l border-gray-200 pl-4 relative">
              <button
                type="button"
                onClick={() => setUserMenuOpen((o) => !o)}
                className="flex items-center gap-2 rounded-lg py-1.5 pr-2 pl-1.5 hover:bg-gray-50 transition-colors"
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
                aria-label="Menu do usuário"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'var(--astra-dark-blue)' }}>
                  <span className="text-white text-sm font-medium">
                    {user?.nome?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-gray-900">{user?.nome || 'Usuário'}</p>
                  <p className="text-xs text-gray-500">{getUserRoleText(user?.role || 'USER')}</p>
                </div>
                <svg className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" aria-hidden onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1.5 w-56 bg-white rounded-xl border border-gray-200 shadow-lg z-20 py-2">
                    {/* Nome e email no topo */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900 truncate">{user?.nome || 'Usuário'}</p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email || ''}</p>
                    </div>
                    {/* Ações */}
                    <div className="py-1">
                      <Link
                        to="/configuracoes"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                      >
                        <svg className="w-4 h-4 text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Configurações
                      </Link>
                      <Link
                        to="/alterar-senha"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                      >
                        <svg className="w-4 h-4 text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        Alterar senha
                      </Link>
                    </div>
                    {/* Separador + Sair */}
                    <div className="border-t border-gray-100 pt-1">
                      <button
                        type="button"
                        onClick={() => { setUserMenuOpen(false); logout(); }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors text-left"
                      >
                        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sair
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}